import _ from 'lodash';

import { PendingChangeKind, PendingChangesActionType } from 'constants/pendingChanges';

// Direct changes are highlighted and counted, recalculated ones (values that only moved as a side effect of a
// person's change) are underlined and not counted. A change without a kind (history recorded before the
// distinction, a change the form itself holds) is Direct.
export const toPendingChangeKind = changeKind =>
  changeKind === PendingChangeKind.RECALCULATED
    ? PendingChangeKind.RECALCULATED
    : PendingChangeKind.DIRECT;

// Components that predate change kinds take a boolean for a pending Direct change; a given kind wins.
export const resolvePendingChangeKind = (changeKind, isDirectChange = false) =>
  changeKind ?? (isDirectChange ? PendingChangeKind.DIRECT : null);

// What a pending-change wrapper shows: the action, and the kind of change behind it. A kind alone marks the
// value modified; a change without a kind is Direct.
export const getPendingChangeWrapperState = (pendingChangesActionType, changeKind = null) => {
  const resolvedActionType =
    pendingChangesActionType ||
    (changeKind ? PendingChangesActionType.MODIFIED : PendingChangesActionType.NO_CHANGES);

  return {
    pendingChangesActionType: resolvedActionType,
    changeKind:
      resolvedActionType === PendingChangesActionType.NO_CHANGES
        ? null
        : toPendingChangeKind(changeKind),
  };
};

const PENDING_CHANGE_KINDS_BY_PRECEDENCE = [
  PendingChangeKind.DIRECT,
  PendingChangeKind.RECALCULATED,
];

// A value built from several fields (a combined LTV/CLTV/HLTV, an address) is Direct when any of them is, otherwise
// Recalculated when any of them is.
export const getCombinedPendingChangeKind = changeKinds =>
  PENDING_CHANGE_KINDS_BY_PRECEDENCE.find(changeKind => changeKinds.includes(changeKind)) ?? null;

// The loan-header lanes carry the pending change already flattened to a value plus its kind. A response with no
// kind (the legacy lane) keeps the prior behaviour: any pending change is Direct.
export const getHeaderPendingChangeKind = (hasPendingChange, changeKind = null) =>
  hasPendingChange ? toPendingChangeKind(changeKind) : null;

/**
 * The kind of pending change behind one or more preview-DTO fields: Direct when modifiedFields lists any of them,
 * Recalculated when modifiedFieldDetails lists any of them as recalculated, null otherwise.
 */
export const getModifiedFieldChangeKind = (entity, fieldNames) => {
  const fieldKeys = _.castArray(fieldNames).map(fieldName => _.upperFirst(fieldName));
  const checkIsAnyListed = listedFields =>
    (listedFields ?? []).some(fieldName => fieldKeys.includes(_.upperFirst(fieldName)));
  const recalculatedFields = (entity?.modifiedFieldDetails ?? [])
    .filter(({ source }) => source === PendingChangeKind.RECALCULATED)
    .map(({ fieldName }) => fieldName);

  return getCombinedPendingChangeKind([
    checkIsAnyListed(entity?.modifiedFields) ? PendingChangeKind.DIRECT : null,
    checkIsAnyListed(recalculatedFields) ? PendingChangeKind.RECALCULATED : null,
  ]);
};
